import i18next from 'i18next'
import { TYPES_USER_ROLES } from '../../utils/constants'
import { hideLoader, REQUEST } from './drawer.actions'
import { gapiErrorAction, gapiDisconnectAction } from './google.actions'
import { fetchStudentListAction } from './student.actions'
import { goToLoginPage } from 'utils'
import * as userApi from 'api/user'
import { LOGIN } from 'config/login'

/*
 * Action types
 */

export const CHECK_NIN = '@@user/CHECK_NIN'
export const CHECK_USER = '@@user/CHECK_USER'
export const FETCH_USER = '@@user/FETCH_USER'
export const LOGIN_USER = '@@user/LOGIN_USER'
export const LINK_USER_CLASSROOM = '@@user/LINK_USER_CLASSROOM'
export const RESET_USER = '@@user/RESET_USER'
export const UPDATE_USER = '@@user/UPDATE_USER'
export const FETCH_AVAILABLE_CLASSROOMS = '@@user/FETCH_AVAILABLE_CLASSROOMS'
export const FETCH_AVAILABLE_COURSES = '@@user/FETCH_AVAILABLE_COURSES'
export const UPDATE_PROMOTION_TERMS = '@@user/UPDATE_PROMOTION_TERMS'

/*
 * Action creators
 */

export const updateUserData = (data) => async (dispatch, getState) => {
  const { user } = getState()
  if (user?.invalidLicense && user.userFlags?.FIRST_STEPS) {
    // eslint-disable-next-line no-param-reassign
    data = { ...data, invalidLicense: user.invalidLicense }
  }

  dispatch({
    payload: data,
    type: UPDATE_USER,
  })
}

export const login = (data) => async (dispatch, getState) => {
  const { user } = getState()
  if (user.loggedIn) {
    return
  }
  dispatch({
    payload: data,
    type: LOGIN_USER,
  })
  try {
    await userApi.login(data)
  } catch (error) {
    dispatch({ type: RESET_USER })
    goToLoginPage({ language: i18next.language })
  }
}

export const checkLogin = () => async (dispatch) => {
  let isLogged = false
  dispatch(hideLoader(true))
  try {
    const { loggedIn } = await userApi.checkLogin()
    isLogged = loggedIn
    if (!loggedIn) {
      if (process.env.NODE_ENV === 'development') {
        await dispatch(
          login({
            password: LOGIN.PASSWORD,
            user: LOGIN.USER,
          })
        )
      } else {
        goToLoginPage({ hasreferal: true, language: i18next.language })
      }
    }
  } catch (error) {
    console.error(error)
  } finally {
    dispatch(hideLoader(false))
  }
  return isLogged
}

export const logout = () => async (dispatch) => {
  try {
    await userApi.sendLogout()
    goToLoginPage({ language: i18next.language })
    dispatch({ type: RESET_USER })
  } catch (error) {
    console.error(error)
  }
}

export const fetchCourses =
  ({ force } = { force: false }) =>
  async (dispatch, getState) => {
    const { user } = getState()
    if (!force && (!user.school || user.course || user.availableCourses)) {
      // avoid request duplication
      return
    }
    dispatch({ type: FETCH_AVAILABLE_COURSES })
    try {
      const response = await userApi.userCourses()
      const data = { availableCourses: response }
      dispatch({
        payload: data,
        type: UPDATE_USER,
      })
    } catch (error) {
      console.error(error)
    }
  }

export const fetchClassrooms =
  ({ force } = { force: false }) =>
  async (dispatch, getState) => {
    const { user } = getState()
    if (
      !force &&
      (!user.school ||
        !user.course ||
        user.classroom ||
        user.availableClassrooms)
    ) {
      // avoid request duplication
      return
    }
    dispatch({ type: FETCH_AVAILABLE_CLASSROOMS })
    try {
      const response = await userApi.userClassrooms()
      const data = { availableClassrooms: response }
      dispatch({
        payload: data,
        type: UPDATE_USER,
      })
    } catch (error) {
      console.error(error)
    }
  }

export const fetchUserData = () => async (dispatch) => {
  dispatch({ type: FETCH_USER })
  try {
    const response = await userApi.getUser()
    const { role } = response
    const { NODE_ENV, REACT_APP_URL_ADMIN } = process.env
    const isAdmin = role === TYPES_USER_ROLES.ADMIN
    const isRelative = role === TYPES_USER_ROLES.RELATIVE
    if (isAdmin) {
      if (NODE_ENV === 'development') {
        window.location.href = REACT_APP_URL_ADMIN
      } else {
        window.location.href = '/admin'
      }
    }
    await dispatch(updateUserData(response))
    await dispatch(fetchCourses())
    await dispatch(fetchClassrooms())
    if (isRelative) {
      await dispatch(fetchStudentListAction())
    }
  } catch (error) {
    console.error(error)
  }
}

export const checkUser = () => async (dispatch) => {
  const isLogged = await dispatch(checkLogin())
  if (isLogged) {
    dispatch(fetchUserData())
  }
}

export const linkGoogleClassroomAction = (data) => async (dispatch) => {
  try {
    const res = await userApi.linkGoogleClassroom({ data })
    if (res) {
      await dispatch({
        isLinked: true,
        type: LINK_USER_CLASSROOM,
      })
    }
  } catch (error) {
    dispatch(gapiErrorAction(error, true))
  } finally {
    const response = await userApi.getUser()
    dispatch(updateUserData(response))
  }
}

export const unlinkGoogleClassroomAction = (vleType) => async (dispatch) => {
  try {
    await userApi.unlinkGoogleClassroom({ vleType })
    dispatch(gapiDisconnectAction())
    dispatch({
      isLinked: false,
      type: LINK_USER_CLASSROOM,
    })
  } catch (error) {
    dispatch(gapiErrorAction(error, false))
  } finally {
    const response = await userApi.getUser()
    dispatch(updateUserData(response))
  }
}

export const changePassword = (newPassword1, newPassword2) => async () => {
  await userApi.changePassword(newPassword1, newPassword2)
}

export const checkPassword = (data) => async (dispatch) => {
  let response
  dispatch(hideLoader(true))
  try {
    response = await userApi.checkPassword(data)
    dispatch({
      payload: response.data,
      type: REQUEST,
    })
  } catch (error) {
    console.error(error)
  } finally {
    dispatch(hideLoader(false))
  }
  return !!response && response.data
}

export const checkPasswordSecurity = (data) => async (dispatch) => {
  dispatch(hideLoader(true))
  try {
    const response = await userApi.getCheckPasswordSecurity(data)
    dispatch({
      payload: response,
      type: REQUEST,
    })
  } finally {
    dispatch(hideLoader(false))
  }
}

export const checkNinAction = (nin) => async (dispatch) => {
  dispatch(hideLoader(true))
  let data
  try {
    dispatch({ type: CHECK_NIN })
    data = await userApi.checkNin(nin)
  } catch (error) {
    return Promise.reject(error)
  } finally {
    dispatch(hideLoader(false))
  }
  return !!data && data
}

export const promotionTermsAcceptedAction =
  (userId, isAccepted) => async (dispatch) => {
    dispatch(hideLoader(true))
    await userApi.patchUserPromotionTerms(userId, isAccepted)
    dispatch({
      payload: isAccepted,
      type: UPDATE_PROMOTION_TERMS,
    })
    dispatch(hideLoader(false))
  }

export const reSendEmail = (userId) => async (dispatch) => {
  let response
  try {
    response = await userApi.reSendEmail(userId)
    dispatch({
      payload: response.data,
      type: REQUEST,
    })
  } catch (error) {
    console.error(error)
  }
}

export const checkEmail = (newEmail) => async (dispatch) => {
  let response
  dispatch(hideLoader(true))
  try {
    response = await userApi.checkEmail(newEmail)
    dispatch({
      payload: response,
      type: REQUEST,
    })
  } finally {
    dispatch(hideLoader(false))
  }
  return response
}

export const checkUserName = (newUsername) => async (dispatch) => {
  let response
  dispatch(hideLoader(true))
  try {
    response = await userApi.checkUserName(newUsername)
    dispatch({
      payload: response,
      type: REQUEST,
    })
  } finally {
    dispatch(hideLoader(false))
  }
  return !!response && response
}
